// utils/orderUtils.js
import { formatAmount, formatAmountRound } from './formatAmount';
import { formatDate } from './dateUtils';
import { getUnpaidExtraJpFeesThb } from './extraJpFees';

export function calculateOrdersSummary(orders, { ignoreDownPayment = false } = {}) {
  let grandTotal = 0;
  const summary = orders.reduce((acc, order) => {
    const { productRate, amount, qty, deliveryFeeJp, isCod, isDown, downRate, transactionFee, websiteTransactionFee } = order;
    let orderTotal = amount * qty + (isCod ? 0 : (deliveryFeeJp || 0));
    let realTotal = 0;
    let transactionAmount = 0;
    transactionAmount += Number(transactionFee) || 0;
    const websiteFeeAmount = Number(websiteTransactionFee) || 0;

    // Extra JP fee lines (e.g. anshin) — flat THB, still owed by the customer
    const extraFeesThb = getUnpaidExtraJpFeesThb(order);

    if (isDown && !ignoreDownPayment) {
      const serviceFee = orderTotal * (downRate / 100);
      const halfPriceCut = orderTotal / 2;
      realTotal += (orderTotal + serviceFee - halfPriceCut);
      grandTotal += (realTotal * productRate + transactionFee + extraFeesThb);
    } else {
      realTotal += orderTotal;
      grandTotal += (orderTotal * productRate + transactionFee + extraFeesThb);
    }

    if (!acc[productRate]) {
      acc[productRate] = {
        downPaymentSubtotal: 0,
        nonDownPaymentSubtotal: 0,
        total: 0,
        realTotal: 0,
        transactionAmount: 0,
        websiteTransactionFeeAmount: 0,
        extraFeesAmount: 0,
        websiteFeeByName: {}, // { webName: { count, total } }
        baseFeeQty: 0,
        transactionQty: 0,
        downPaymentFee: 0,
        nonDownPaymentFee: 0,
        orders: [],
        hasAnyDownPaymentOrders: false,
        areAllOrdersDownPayment: true,
      };
    }

    acc[productRate].orders.push(order);
    acc[productRate].total += orderTotal;
    acc[productRate].realTotal += realTotal;
    acc[productRate].transactionAmount += transactionAmount;
    acc[productRate].websiteTransactionFeeAmount += websiteFeeAmount;
    acc[productRate].extraFeesAmount += extraFeesThb;
    acc[productRate].transactionQty++;

    const baseFeeForOrder = (Number(transactionFee) || 0) - websiteFeeAmount;
    if (baseFeeForOrder > 0) acc[productRate].baseFeeQty++;
    if (websiteFeeAmount > 0 && order.website) {
      const wn = order.website;
      if (!acc[productRate].websiteFeeByName[wn]) acc[productRate].websiteFeeByName[wn] = { count: 0, total: 0 };
      acc[productRate].websiteFeeByName[wn].count++;
      acc[productRate].websiteFeeByName[wn].total += websiteFeeAmount;
    }

    if (isDown) {
      acc[productRate].downPaymentSubtotal += orderTotal;
      acc[productRate].downPaymentFee += transactionAmount;
      acc[productRate].hasAnyDownPaymentOrders = true;
    } else {
      acc[productRate].nonDownPaymentSubtotal += orderTotal;
      acc[productRate].nonDownPaymentFee += transactionAmount;
      acc[productRate].areAllOrdersDownPayment = false;
    }

    return acc;
  }, {});

  Object.values(summary).forEach(rateSummary => {
    if (rateSummary.orders.length === 0) {
      rateSummary.areAllOrdersDownPayment = false;
    }
  });

  return { summary, grandTotal };
}

export function generateFirstFollowUpText(summary, grandTotal, gender) {
  if (!summary || Object.keys(summary).length === 0) return '';

  let text = `สวัสดี${gender} คุณลูกค้า\n\n`;
  text += `ขออนุญาตติดตามยอดค้างชำระรายการประมูล (ครั้งที่ 2) นะ${gender} เนื่องจากรายการนี้ผ่านมาค่อนข้างนานแล้ว แต่คุณลูกค้ายังไม่ได้มีการชำระเข้ามา😭${gender}`;

  Object.keys(summary).forEach(rate => {
    const { orders, total, transactionAmount, websiteTransactionFeeAmount, extraFeesAmount = 0 } = summary[rate];
    const date = formatDate(orders[0]?.orderDate);

    text += `\n\n📌 ยอดค้างวันที่ ${date}  เรท ${rate} \n`;
    orders.forEach(order => {
      const amountYen = formatAmountRound(order.amount * order.qty);
      const hasDelivery = !order.isCod && order.deliveryFeeJp;
      const amountDisplay = hasDelivery
        ? `${amountYen}+${formatAmountRound(order.deliveryFeeJp)}`
        : amountYen;
      text += `▪️ ${order.url} (${amountDisplay}¥)\n`;
    });

    const totalThb = (total * Number(rate) + transactionAmount + extraFeesAmount).toFixed(2);
    const feeBreakdown = websiteTransactionFeeAmount > 0 ? ` (รวมค่าบริการเว็บไซต์ ${websiteTransactionFeeAmount}฿)` : '';
    const extraFeesText = extraFeesAmount > 0 ? ` + ค่าบริการเพิ่มเติม ${extraFeesAmount}฿` : '';
    text += `\nยอดรวม (${formatAmountRound(total)}¥ x ${rate}) + ค่าบริการ ${transactionAmount}฿${feeBreakdown}${extraFeesText} = ${totalThb} บาท`;
  });

  if (Object.keys(summary).length > 1) {
    text += `\n\n=================\n💰 ยอดรวมทั้งหมด: ${grandTotal.toFixed(2)} บาท`;
  }

  return text;
}

export function generateFinalNoticeText(summary, grandTotal, gender, advanceAmount = 0) {
  if (!summary || Object.keys(summary).length === 0) return '';

  const rateKeys = Object.keys(summary);
  const hasMultipleRates = rateKeys.length > 1;

  let text = `[‼️FINAL NOTICE - แจ้งเตือนครั้งสุดท้าย‼️]\n\n`;
  text += `เรียนคุณลูกค้า เนื่องจากรายการประมูลชนะของท่านค้างชำระมาครบ 1 สัปดาห์ และมีการติดตามไปแล้ว 2 ครั้งแต่ยังไม่มีการชำระหรือตอบกลับ`;

  rateKeys.forEach(rate => {
    const { orders, total, transactionAmount, websiteTransactionFeeAmount, extraFeesAmount = 0 } = summary[rate];
    const date = formatDate(orders[0]?.orderDate);

    text += `\n\n📌 ยอดค้างวันที่ ${date}  เรท ${rate} \n`;
    orders.forEach(order => {
      const amountYen = formatAmountRound(order.amount * order.qty);
      const hasDelivery = !order.isCod && order.deliveryFeeJp;
      const amountDisplay = hasDelivery
        ? `${amountYen}+${formatAmountRound(order.deliveryFeeJp)}`
        : amountYen;
      text += `▪️ ${order.url} (${amountDisplay}¥)\n`;
    });

    const totalThb = (total * Number(rate) + transactionAmount + extraFeesAmount).toFixed(2);
    const feeBreakdown = websiteTransactionFeeAmount > 0 ? ` (รวมค่าบริการเว็บไซต์ ${websiteTransactionFeeAmount}฿)` : '';
    const extraFeesText = extraFeesAmount > 0 ? ` + ค่าบริการเพิ่มเติม ${extraFeesAmount}฿` : '';
    text += `\nยอดรวม (${formatAmountRound(total)}¥ x ${rate}) + ค่าบริการ ${transactionAmount}฿${feeBreakdown}${extraFeesText} = ${totalThb} บาท`;
  });

  if (hasMultipleRates || advanceAmount > 0) {
    text += `\n\n=================\n💰 ยอดรวม ${grandTotal.toFixed(2)} บาท`;
    if (advanceAmount > 0) {
      const netAmount = Math.max(0, grandTotal - advanceAmount);
      text += `\n➖มียอดฝากไว้ : ${advanceAmount.toFixed(2)} บาท\n━━━━━━━━━━━━\nยอดที่ต้องชำระ: ${netAmount.toFixed(2)} บาท`;
    }
  }

  return text;
}

export function generateFinalNoticeUltimateText(summary, grandTotal, gender, advanceAmount = 0, paymentUrl = null) {
  if (!summary || Object.keys(summary).length === 0) return '';

  const rateKeys = Object.keys(summary);
  const hasMultipleRates = rateKeys.length > 1;

  let text = `[‼️ FINAL NOTICE ขั้นสุดท้าย ‼️]\n\n`;
  text += `เรียนคุณลูกค้า รายการประมูลชนะของท่านค้างชำระมาครบ 2 สัปดาห์ ทางร้านได้ทำการติดตามไปแล้ว 3 ครั้งแต่ยังไม่ได้รับการชำระหรือตอบกลับ\n\nหากไม่มีการชำระภายใน 7 วัน ทางร้านจะดำเนินการจัดส่งเอกสารเรียกเก็บหนี้ไปยังที่อยู่ที่ท่านแจ้งไว้`;

  rateKeys.forEach(rate => {
    const { orders, total, transactionAmount, websiteTransactionFeeAmount, extraFeesAmount = 0 } = summary[rate];
    const date = formatDate(orders[0]?.orderDate);

    text += `\n\n📌 ยอดค้างวันที่ ${date}  เรท ${rate} \n`;
    orders.forEach(order => {
      const amountYen = formatAmountRound(order.amount * order.qty);
      const hasDelivery = !order.isCod && order.deliveryFeeJp;
      const amountDisplay = hasDelivery
        ? `${amountYen}+${formatAmountRound(order.deliveryFeeJp)}`
        : amountYen;
      text += `▪️ ${order.url} (${amountDisplay}¥)\n`;
    });

    const totalThb = (total * Number(rate) + transactionAmount + extraFeesAmount).toFixed(2);
    const feeBreakdown = websiteTransactionFeeAmount > 0 ? ` (รวมค่าบริการเว็บไซต์ ${websiteTransactionFeeAmount}฿)` : '';
    const extraFeesText = extraFeesAmount > 0 ? ` + ค่าบริการเพิ่มเติม ${extraFeesAmount}฿` : '';
    text += `\nยอดรวม (${formatAmountRound(total)}¥ x ${rate}) + ค่าบริการ ${transactionAmount}฿${feeBreakdown}${extraFeesText} = ${totalThb} บาท`;
  });

  if (hasMultipleRates || advanceAmount > 0) {
    text += `\n\n=================\n💰 ยอดรวม ${grandTotal.toFixed(2)} บาท`;
    if (advanceAmount > 0) {
      const netAmount = Math.max(0, grandTotal - advanceAmount);
      text += `\n➖มียอดฝากไว้ : ${advanceAmount.toFixed(2)} บาท\n━━━━━━━━━━━━\nยอดที่ต้องชำระ: ${netAmount.toFixed(2)} บาท`;
    }
  }

  if (paymentUrl) {
    text += `\n\n🔗 ชำระเงินได้ที่ลิงก์นี้${gender}:\n${paymentUrl}`;
  }

  return text;
}

export function generateSummaryText(summary, grandTotalState, gender, isTama, isPayFull = false) {
  let text = ``;
  if (summary && Object.keys(summary).length > 0) {
    text = `ขออนุญาตสรุปยอดให้นะ${gender}`;
    text += `\n`;

    Object.keys(summary).forEach(rate => {
      const { orders, downPaymentSubtotal, nonDownPaymentSubtotal, hasAnyDownPaymentOrders, areAllOrdersDownPayment, realTotal, transactionQty, transactionAmount, websiteTransactionFeeAmount, websiteFeeByName, baseFeeQty, downPaymentFee, nonDownPaymentFee, total, extraFeesAmount = 0 } = summary[rate];
      const baseFeeOnly = transactionAmount - (websiteTransactionFeeAmount || 0);
      const websiteFeeEntries = Object.entries(websiteFeeByName || {});
      const downRate = orders[0].downRate; // Assuming downRate is consistent within each rate group
      if (Object.keys(summary).length > 1) {
        text += `\n=================\n`;
        text += `Rate ${rate}\n`;
      }
      orders.forEach(order => {
        text += `${order.url}\n${order.amount * order.qty}${!order.isCod && order.deliveryFeeJp !== null ? ` + ${order.deliveryFeeJp}` : ''}${!isPayFull && order.isDown ? ' (มัดจำ 50%)' : ''}\n`;
      });
      if (!isPayFull && hasAnyDownPaymentOrders) {
        text += `-----------------\n`;
        text += `ยอดรวมที่ต้องการจะมัดจำ = ${formatAmount(downPaymentSubtotal)} * ${rate} = ${formatAmount(downPaymentSubtotal * rate)} บาท\n\n`;
        text += `ยอดชำระมัดจำ ${formatAmount(downPaymentSubtotal * rate)} / 2 = ${formatAmount((downPaymentSubtotal * rate) / 2)} บาท\n`;
        text += `ค่าบริการมัดจำ ${formatAmount(downPaymentSubtotal * rate)} * ${downRate / 100} = ${formatAmount((downPaymentSubtotal * rate) * (downRate / 100))} บาท\n\n`;
        text += `รวมยอดชำระรอบแรก = ${formatAmount(downPaymentSubtotal * rate / 2)} + ${formatAmount((downPaymentSubtotal * rate) * (downRate / 100))} ${isTama ? ` + ${downPaymentFee} `: ""}${extraFeesAmount > 0 ? ` + ค่าส่งตรวจ ${formatAmount(extraFeesAmount)} ` : ''}= ${formatAmount((downPaymentSubtotal * rate / 2) + (downPaymentSubtotal * rate) * (downRate / 100) + downPaymentFee + extraFeesAmount)} บาท\n`;
        if (!areAllOrdersDownPayment) {
          text += `-----------------\n`;
          text += `ยอดรวมที่ไม่มัดจำทั้งหมด = ${formatAmount(nonDownPaymentSubtotal)} * ${rate} ${isTama ? ` + ${nonDownPaymentFee} `: ""}= ${formatAmount(nonDownPaymentSubtotal * rate + nonDownPaymentFee)} บาท\n\n`;
          text += `ยอดรวมทั้งหมด = ${formatAmount((downPaymentSubtotal * rate / 2) + (downPaymentSubtotal * rate) * (downRate / 100) + downPaymentFee + extraFeesAmount)} + ${formatAmount(nonDownPaymentSubtotal * rate + nonDownPaymentFee)} = ${formatAmount(realTotal * rate + downPaymentFee + nonDownPaymentFee + extraFeesAmount)} บาท\n`;
        }
        //text += `รวมค่าค่าบริการ ${transactionQty} รายการ เป็นยอดทั้งหมด ${transactionAmount} บาท\n`;
        //text += `รวมทั้งหมดเป็นยอด ${formatAmount(realTotal * rate)} + ${nonDownPaymentFee + downPaymentFee} = ${formatAmount(realTotal * rate + nonDownPaymentFee + downPaymentFee)} บาท\n`;
      } else {
        // When isPayFull is true, use total (includes all orders) instead of nonDownPaymentSubtotal
        const displaySubtotal = isPayFull ? total : nonDownPaymentSubtotal;
        
        if(isTama){
          const hasBothFees = baseFeeOnly > 0 && websiteTransactionFeeAmount > 0;
          text += `-----------------\n`;
          text += `รวมค่าสินค้าเป็นยอด ${displaySubtotal} เยน * ${rate} = ${(displaySubtotal * rate).toFixed(2)} บาท\n\n`;
          if (hasBothFees) {
            text += `ค่าบริการสั่งซื้อ\n`;
            text += `- ค่าบริการ ${baseFeeQty} รายการ เป็นยอดทั้งหมด ${baseFeeOnly} บาท\n`;
            websiteFeeEntries.forEach(([webName, { count, total: wTotal }]) => {
              text += `- ค่าบริการพิเศษสำหรับรายการ ${webName} ${count} รายการ เป็นยอด ${wTotal} บาท\n`;
            });
          } else if (baseFeeOnly > 0) {
            text += `รวมค่าค่าบริการ ${transactionQty} รายการ เป็นยอดทั้งหมด ${baseFeeOnly} บาท\n`;
          } else {
            websiteFeeEntries.forEach(([webName, { count, total: wTotal }]) => {
              text += `ค่าบริการพิเศษสำหรับรายการ ${webName} ${count} รายการ เป็นยอด ${wTotal} บาท\n`;
            });
          }
          if (extraFeesAmount > 0) {
            text += `ค่าบริการส่งตรวจ ${formatAmount(extraFeesAmount)} บาท\n`;
          }
          text += `\nรวมทั้งหมดเป็นยอด ${(displaySubtotal * rate + transactionAmount + extraFeesAmount).toFixed(2)} บาท\n`;
        }else{
          text += `-----------------\n`;
          const productThb = displaySubtotal * rate;
          text += `รวมทั้งหมดเป็นยอด ${displaySubtotal} เยน * ${rate} = ${productThb.toFixed(2)} บาท`;
          if (websiteTransactionFeeAmount > 0) {
            websiteFeeEntries.forEach(([webName, { count, total: wTotal }]) => {
              text += `\nค่าบริการพิเศษสำหรับรายการ ${webName} ${count} รายการ เป็นยอด ${wTotal} บาท`;
            });
          }
          if (extraFeesAmount > 0) {
            text += `\nค่าบริการส่งตรวจ ${formatAmount(extraFeesAmount)} บาท`;
          }
          if (websiteTransactionFeeAmount > 0 || extraFeesAmount > 0) {
            text += `\n\nรวมทั้งหมดเป็นยอด ${(productThb + websiteTransactionFeeAmount + extraFeesAmount).toFixed(2)} บาท`;
          }
        }
      }
    });
    if (Object.keys(summary).length > 1) {
      text += `\n=================\n`;
      text += `ยอดรวมทั้งหมด ${(grandTotalState).toFixed(2)} บาท`;
    }
  }
  return text;
}
